import React from "react"
import { useNavigate } from "react-router-dom"
import { User, IndianRupee, Calendar, CalendarCheck } from "lucide-react"
import { Button } from "src/components/ui/button"
import { formatMoney, formatDate } from "../constants"

// A deal card for the Kanban board. Draggable only when the viewer can move
// deals between stages (deals/update) — read-only roles can still click through.
export function DealCard({ deal, onDragStart, onDragEnd, dragging, canDrag = true }) {
  const navigate = useNavigate()

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? (e) => onDragStart?.(e, deal) : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={() => navigate(`/deals/${deal.id}`)}
      className={[
        "group cursor-pointer rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition",
        "hover:border-slate-300 hover:shadow-md",
        dragging ? "opacity-40" : "",
      ].join(" ")}
    >
      <div className="mb-1.5 line-clamp-2 text-[13px] font-medium text-slate-800">
        {deal.name || `Deal #${deal.id}`}
      </div>

      {deal.lead && (
        <div className="mb-2 flex items-center gap-1.5 text-[12px] text-slate-500">
          <User className="h-3 w-3 shrink-0" />
          <span className="truncate">{deal.lead.name}</span>
        </div>
      )}

      <div className="flex items-center justify-between text-[12px]">
        <span className="inline-flex items-center gap-1 font-medium text-slate-700">
          <IndianRupee className="h-3 w-3" />
          {deal.value ? formatMoney(deal.value).replace("₹", "") : "—"}
        </span>
        {deal.expected_close_on && (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Calendar className="h-3 w-3" />
            {formatDate(deal.expected_close_on)}
          </span>
        )}
      </div>

      {deal.owner?.full_name && (
        <div className="mt-2 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-400">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-[8px] font-semibold text-slate-600">
            {deal.owner.full_name.slice(0, 1).toUpperCase()}
          </span>
          {deal.owner.full_name}
        </div>
      )}

      {deal.booking_id && (
        <Button
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${deal.booking_id}`) }}
          className="mt-2 flex h-auto w-full items-center justify-center gap-1 rounded-md bg-emerald-50 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 hover:text-emerald-700"
        >
          <CalendarCheck className="h-3 w-3" /> Converted to booking →
        </Button>
      )}
    </div>
  )
}
